import type { ReactNode } from 'react';
import { MissingValue } from './MissingValue';
import { CopyField } from './CopyField';

// ------------------------------------------------------------------
// DataList — story 2-138 (P10 §2.4, page-composition contract).
// ------------------------------------------------------------------
//
// Pure label/value presentation — the primitive every page's "facts" block
// (a report's Type/Generated/Approved row, a site's address/owner block, a
// version's metadata) should reach for instead of hand-rolling its own `dl`.
// No fetching, no module knowledge, no domain nouns (P10 §2.4's own scope
// line): callers hand in `{ label, value }` pairs and DataList decides only
// how they READ — wrapping, copy actions, a missing value, density,
// responsive stacking.
//
// COMPOSES, DOES NOT REINVENT (both call sites named in this story's own
// dispatch):
//   - `MissingValue` (story 2-137) for any item whose `value` is
//     `undefined`/`null`/`''` — the ONE glyph + accessible name for "this
//     field has no value" stays the one place that decision lives.
//   - `CopyField` (story 6-1a) for `copyable` items — a one-tap-copy row
//     reuses that primitive's clipboard handling/confirmation instead of a
//     second copy-button implementation. `showLabel={false}` because
//     DataList's own `<dt>` already renders the visible label; CopyField's
//     internal label stays for the accessible name only.
//
// A real `<dl>`, not a styled grid of `<div>`s — label/value pairing is
// exactly what that element means, and it is what lets a screen-reader user
// navigate the list by term the same way a sighted user scans it.
//
// `breakAll` mirrors `contentStressMatrix.ts`'s documented convention for a
// long whitespace-free token (an id/hash/slug): `break-all`, not the plain
// prose wrapping every other value gets, because prose wrapping only ever
// breaks at a space and an identifier has none.
export interface DataListItem {
  /** Stable React key — falls back to `label` when omitted (fine whenever
   * labels in one list are unique, the common case). */
  key?: string;
  /** The field name, e.g. "Due date". Rendered as the row's `<dt>` and
   * folded into `MissingValue`'s accessible name when the value is absent. */
  label: string;
  /**
   * The value. A plain string/number renders as text (or, with `copyable`,
   * through `CopyField`); a `ReactNode` lets a caller hand in an
   * already-composed value (a `Badge`, a link) that DataList never tries to
   * reinterpret. `undefined`/`null`/`''` renders through `MissingValue` —
   * DataList never lets an absent value fall through to silent blank space.
   */
  value?: ReactNode;
  /** Renders the value through `CopyField` (one-tap copy) instead of static
   * text. Only meaningful when `value` is a plain string — a composed
   * `ReactNode` is left as the caller's own affordance, not wrapped again. */
  copyable?: boolean;
  /** The value is a long, whitespace-free token that must break mid-word
   * rather than overflow (see file header). Only meaningful for a plain
   * string value. */
  breakAll?: boolean;
}

export interface DataListProps {
  items: DataListItem[];
  /** Row rhythm: 'comfortable' (default) for a page's own metadata block,
   * 'compact' for a denser rail/summary context. Spacing only — the
   * existing Tailwind spacing scale already used throughout the DS, no new
   * geometry token (2-131/2-136 own that layer; this story reuses it). */
  density?: 'comfortable' | 'compact';
  /** Responsive column count at `sm:` and above. Always a single stacked
   * column below `sm` regardless of this value — the "responsive stacking"
   * requirement is unconditional, not opt-in. Default 1 (always stacked). */
  columns?: 1 | 2 | 3;
  className?: string;
  'data-testid'?: string;
}

const COLUMN_CLASS: Record<1 | 2 | 3, string> = {
  1: '',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-2 lg:grid-cols-3',
};

const GAP_CLASS: Record<'comfortable' | 'compact', string> = {
  comfortable: 'gap-x-8 gap-y-4',
  compact: 'gap-x-6 gap-y-2',
};

export function DataList({
  items,
  density = 'comfortable',
  columns = 1,
  className = '',
  'data-testid': testId,
}: DataListProps): JSX.Element {
  return (
    <dl className={`grid ${COLUMN_CLASS[columns]} ${GAP_CLASS[density]} ${className}`} data-testid={testId}>
      {items.map((item, index) => {
        const isEmpty = item.value === undefined || item.value === null || item.value === '';
        const isPlainString = typeof item.value === 'string';
        return (
          <div key={item.key ?? item.label ?? index} className="min-w-0">
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">{item.label}</dt>
            <dd
              className={`mt-0.5 min-w-0 text-sm text-foreground ${
                item.breakAll && isPlainString ? 'break-all font-mono text-xs' : ''
              }`}
            >
              {isEmpty ? (
                <MissingValue label={item.label} />
              ) : item.copyable && isPlainString ? (
                <CopyField label={item.label} showLabel={false} value={item.value as string} />
              ) : (
                item.value
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
